<template>
    <div class="size-panel">
        <div class="header">创建设计</div>

        <div class="search">
            <el-input v-model="searchKeyword" :prefix-icon="Search" placeholder="搜索全部尺寸" clearable />
        </div>

        <div class="section-title">自定义尺寸</div>
        <div class="custom-row">
            <el-input v-model.number="width" placeholder="宽" class="wh-input" />
            <span class="x">×</span>
            <el-input v-model.number="height" placeholder="高" class="wh-input" />
            <el-select v-model="unit" class="unit-select" style="margin:0 10px;">
                <el-option label="px" value="px" />
                <el-option label="cm" value="cm" />
                <el-option label="mm" value="mm" />
            </el-select>
            <el-button type="primary" class="create-btn" @click="onCreate()"
                style="background-color:#2a66ff;">创建</el-button>
        </div>

        <div class="section-title mt-12">系统推荐尺寸</div>
        <el-scrollbar class="list-wrap">
            <div v-for="item in filteredList" :key="item.name" class="size-item cursor-pointer"
                @click="applyPreset(item)">
                <div class="name">{{ item.name }}</div>
                <div class="dim">{{ item.w }} × {{ item.h }} {{ item.unit }}</div>
            </div>
            <div v-if="filteredList.length === 0" class="empty">未找到匹配尺寸</div>
        </el-scrollbar>
    </div>
</template>
<script lang="ts" setup>
import { ref, computed, } from 'vue'
import { ElInput, ElButton, ElSelect, ElOption, ElScrollbar, ElIcon } from 'element-plus'
import { Search, RefreshRight } from '@element-plus/icons-vue'
import { createDesign } from '@/api/affairs'
import { getSystomDpi, mm2px } from '@/common/tool'
const searchKeyword = ref('')
const width = ref<number | null>(500)
const height = ref<number | null>(500)
const unit = ref<'px' | 'cm' | 'mm' | 'in'>('px')
import { useUserStore } from '@/stores/user'
const props = defineProps({
    type: {
        type: String,
        default: ''
    }
})
const userStore = useUserStore()
const presets = ref([
    { name: '手机海报', w: 1242, h: 2208, unit: 'px' },
    { name: '横版海报', w: 900, h: 500, unit: 'px' },
    { name: '小红书配图', w: 1242, h: 1656, unit: 'px' },
    { name: '公众号首页图', w: 900, h: 383, unit: 'px' },
    { name: '公众号次图', w: 500, h: 500, unit: 'px' },
    { name: '文章长图', w: 1000, h: 1500, unit: 'px' },
    { name: 'PPT (16:9)', w: 1920, h: 1080, unit: 'px' },
    { name: '竖版直播背景', w: 1242, h: 2690, unit: 'px' },
    { name: '竖版视频封面', w: 1242, h: 2208, unit: 'px' },
    { name: '横版视频封面', w: 1920, h: 1080, unit: 'px' },
    { name: '商品主图', w: 800, h: 800, unit: 'px' },
    { name: '电商详情页', w: 750, h: 1000, unit: 'px' },
    { name: 'PC端广告首页', w: 1920, h: 150, unit: 'px' },
    { name: '手机端广告首页', w: 750, h: 1000, unit: 'px' },
    { name: '店招', w: 1920, h: 150, unit: 'px' },
    { name: '电商横版海报', w: 750, h: 930, unit: 'px' },
    { name: '电商竖版海报', w: 750, h: 950, unit: 'px' },
    { name: '电商全版海报', w: 1920, h: 700, unit: 'px' },
    { name: '日签', w: 1242, h: 2208, unit: 'px' },
    { name: '小程序封面图', w: 520, h: 416, unit: 'px' },
])

const filteredList = computed(() => {
    const k = searchKeyword.value.trim().toLowerCase()
    if (!k) return presets.value
    return presets.value.filter(p =>
        p.name.toLowerCase().includes(k) || `${p.w}x${p.h}${p.unit}`.includes(k)
    )
})

const swapWH = () => {
    const w = width.value
    width.value = height.value
    height.value = w
}

const applyPreset = (p: { w: number; h: number; unit: 'px' | 'cm' | 'mm' | 'in'; name: string }) => {
    width.value = p.w
    height.value = p.h
    unit.value = p.unit
    onCreate()
}

const emit = defineEmits<{
    (e: 'create', payload: { width: number | null; height: number | null; unit: string, id: any }): void
}>()

const onCreate = async () => {
    if (userStore.isLoggedIn) {
        let h = height.value || 0
        let w = width.value || 0
        let dpi = 72
        if (unit.value == 'cm') {
            //@ts-ignore
            // dpi = getSystomDpi(w, h, 1)
            w = mm2px(w * 10, dpi)
            h = mm2px(h * 10, dpi)
        }
        if (unit.value == 'mm') {
            //@ts-ignore
            // dpi = getSystomDpi(w, h, 2)
            w = mm2px(w, dpi)
            h = mm2px(h, dpi)
        }
        const res = await createDesign({
            height: h,
            template_id: 0,
            name: '未命名模板',
            width: w,
        })
        localStorage.setItem('pk', String(res.data?.data?.pk ?? ''))
        localStorage.setItem('createNewTemplateDpi', String(dpi))
        const params = new URLSearchParams({
            id: String(res.data?.data?.pk ?? ''),
            mode: 'user',
            type: props.type,
        })
        emit('create', { width: w, height: h, unit: unit.value, id: res.data?.data?.pk })
        window.open(`/editor?${params.toString()}`, '_blank')
    } else {
        const params = new URLSearchParams({
            mode: 'create',
            category_id: '0',
            type: 'poster',
            width: String(width.value ?? ''),
            height: String(height.value ?? ''),
            unit: String(unit.value),
        })
        emit('create', { width: width.value || 0, height: height.value || 0, unit: unit.value, id: null })
        window.open(`/editor?${params.toString()}`, '_blank')
    }

}

</script>
<style lang="scss" scoped>
.size-panel {
    width: 400px;
    padding: 12px 12px 8px;
}

.header {
    font-size: 14px;
    color: #38393b;
    margin-bottom: 8px;
}

.search :deep(.el-input) {
    width: 100%;
}

.section-title {
    font-size: 12px;
    color: #8a8f99;
    margin-top: 8px;
}

.custom-row {
    margin-top: 8px;
    display: flex;
    align-items: center;
}

.wh-input {
    width: 90px;
}

.x {
    margin: 0 8px;
    color: #666;
}

.swap-btn {
    margin-left: 8px;
}

.custom-bottom {
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.unit-select {
    width: 120px;
}

.create-btn {
    min-width: 80px;
}

.list-wrap {
    margin-top: 8px;
}

.size-item {
    padding: 8px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #eee;
}

.size-item:last-child {
    border-bottom: none;
}

.name {
    color: #38393b;
}

.dim {
    color: #8a8f99;
}

.empty {
    padding: 12px 0;
    color: #8a8f99;
    text-align: center;
}

.mt-12 {
    margin-top: 12px;
}
</style>
